import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import clsx from "clsx";
import { AlertTriangle, CheckCircle2, CircleDashed, Info, XCircle } from "lucide-react";
import { forwardRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode } from "react";
import type { ValidationState } from "@/lib/api";

import { t, msg } from "@/lib/i18n";
export const cx = clsx;

// ── Button ────────────────────────────────────────────────────────────
type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
type ButtonSize = "sm" | "md";

export const Button = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; size?: ButtonSize; icon?: ReactNode; loading?: boolean }>(
  function Button({ variant = "secondary", size = "md", icon, loading, className, children, disabled, ...props }, ref) {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={cx(
          "inline-flex items-center justify-center gap-1.5 rounded-lg font-medium whitespace-nowrap transition-[background,box-shadow,color] select-none",
          "disabled:opacity-50 disabled:pointer-events-none",
          size === "sm" ? "h-7 px-2.5 text-[12.5px]" : "h-8 px-3 text-[13px]",
          variant === "primary" && "bg-ink text-ink-fg hover:opacity-90 shadow-sm",
          variant === "secondary" && "bg-surface text-fg border border-border hover:bg-surface-2 shadow-[0_1px_0_rgba(0,0,0,0.03)]",
          variant === "ghost" && "text-fg-muted hover:text-fg hover:bg-surface-2",
          variant === "danger" && "bg-danger text-white hover:opacity-90",
          className,
        )}
        {...props}
      >
        {loading ? <span className="size-3.5 rounded-full border-2 border-current border-r-transparent animate-spin" /> : icon}
        {children}
      </button>
    );
  },
);

// ── Input ─────────────────────────────────────────────────────────────
export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean; suffix?: ReactNode; leading?: ReactNode }>(
  function Input({ className, invalid, suffix, leading, ...props }, ref) {
    return (
      <div
        className={cx(
          "flex items-center h-8 rounded-lg border bg-surface transition-colors focus-within:ring-2",
          // markierte Felder bleiben auch im Fokus rot erkennbar
          invalid ? "border-danger/60 focus-within:border-danger focus-within:ring-danger/15" : "border-border focus-within:border-fg-muted focus-within:ring-fg/10",
          className,
        )}
      >
        {leading && <span className="pl-2.5 text-fg-subtle">{leading}</span>}
        <input ref={ref} aria-invalid={invalid || undefined} className="w-full min-w-0 bg-transparent px-2.5 outline-none placeholder:text-fg-subtle tabular-nums" {...props} />
        {suffix && <span className="pr-2.5 text-fg-subtle text-[12px]">{suffix}</span>}
      </div>
    );
  },
);

// ── Badge ─────────────────────────────────────────────────────────────
type Tone = "neutral" | "success" | "warning" | "danger" | "info" | "coral";
const TONES: Record<Tone, string> = {
  neutral: "bg-surface-2 text-fg-muted border-border",
  success: "bg-success-soft text-success border-success/20",
  warning: "bg-warning-soft text-warning border-warning/20",
  danger: "bg-danger-soft text-danger border-danger/20",
  info: "bg-info-soft text-info border-info/20",
  coral: "bg-coral-soft text-coral border-coral/25",
};
export function Badge({ tone = "neutral", children, className, icon }: { tone?: Tone; children: ReactNode; className?: string; icon?: ReactNode }) {
  return (
    <span className={cx("inline-flex items-center gap-1 h-5 px-1.5 rounded-md border text-[11.5px] font-medium whitespace-nowrap", TONES[tone], className)}>
      {icon}
      {children}
    </span>
  );
}

export type PillTone = Exclude<Tone, "coral"> | "transit" | "delivery";
const PILLS: Record<PillTone, string> = {
  /** beim Dienstleister unterwegs */
  transit: "bg-[var(--pill-transit)] text-[var(--pill-transit-fg)]",
  /** in Zustellung – kurz vor dem Ziel */
  delivery: "bg-[var(--pill-delivery)] text-[var(--pill-delivery-fg)]",
  neutral: "bg-[var(--pill-neutral)] text-[var(--pill-neutral-fg)]",
  info: "bg-[var(--pill-info)] text-[var(--pill-info-fg)]",
  success: "bg-[var(--pill-success)] text-[var(--pill-success-fg)]",
  warning: "bg-[var(--pill-warning)] text-[var(--pill-warning-fg)]",
  danger: "bg-[var(--pill-danger)] text-[var(--pill-danger-fg)]",
};
/** Status als gefüllte Pille mit kleinem Icon (Auftragsstatus, Zustellung) */
export function StatusPill({ tone = "neutral", icon, children, className }: { tone?: PillTone; icon?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <span className={cx("inline-flex items-center gap-1.5 h-6 pl-1.5 pr-2 rounded-lg text-[12px] font-medium whitespace-nowrap [&>svg]:size-3.5 [&>svg]:shrink-0", PILLS[tone], className)}>
      {icon}
      {children}
    </span>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="inline-flex items-center h-[18px] min-w-[18px] justify-center px-1 rounded border border-border bg-surface-2 text-[10.5px] font-medium text-fg-muted font-sans">{children}</kbd>;
}

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx("rounded-xl border border-border bg-surface shadow-[0_1px_2px_rgba(0,0,0,0.03)]", className)}>{children}</div>;
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cx("skeleton", className)} />;
}

// ── Validierung ───────────────────────────────────────────────────────
export const VALIDATION_META: Record<ValidationState, { label: string; tone: Tone; Icon: typeof CheckCircle2 }> = {
  valid: { label: msg("Vollständig"), tone: "success", Icon: CheckCircle2 },
  warnings: { label: msg("Hinweise"), tone: "warning", Icon: AlertTriangle },
  invalid: { label: msg("Blockiert"), tone: "danger", Icon: XCircle },
  unchecked: { label: msg("Ungeprüft"), tone: "neutral", Icon: CircleDashed },
};

export function SeverityIcon({ severity, className }: { severity: "error" | "warning" | "info"; className?: string }) {
  if (severity === "error") return <XCircle className={cx("size-4 text-danger shrink-0", className)} />;
  if (severity === "warning") return <AlertTriangle className={cx("size-4 text-warning shrink-0", className)} />;
  return <Info className={cx("size-4 text-info shrink-0", className)} />;
}

export function ValidationBadge({ status }: { status: ValidationState }) {
  const m = VALIDATION_META[status];
  return (
    <Badge tone={m.tone} icon={<m.Icon className="size-3" />}>
      {t(m.label)}
    </Badge>
  );
}

/** Stammdatenqualität 0–100 als kompakter Balken */
export function ScoreBar({ score, className }: { score: number; className?: string }) {
  const color = score >= 90 ? "bg-success" : score >= 70 ? "bg-warning" : "bg-danger";
  return (
    <div className={cx("flex items-center gap-2", className)} aria-label={t("Datenqualität {score} %", { score })}>
      <div className="h-1.5 w-14 rounded-full bg-surface-2 overflow-hidden">
        <div className={cx("h-full rounded-full transition-[width] duration-500", color)} style={{ width: `${score}%` }} />
      </div>
      <span className="text-[12px] text-fg-muted tabular-nums w-8">{score}%</span>
    </div>
  );
}

export function Tip({ content, children, side = "top" }: { content: ReactNode; children: ReactNode; side?: "top" | "bottom" | "left" | "right" }) {
  return (
    <TooltipPrimitive.Root delayDuration={250}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content side={side} sideOffset={6} className="z-50 max-w-xs rounded-md bg-ink text-ink-fg px-2 py-1 text-[12px] shadow-lg animate-fade-in">
          {content}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

export function EmptyState({ icon, title, children, action }: { icon: ReactNode; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 px-6">
      <div className="size-11 rounded-xl bg-surface-2 border border-border grid place-items-center text-fg-muted mb-3">{icon}</div>
      <p className="font-semibold text-[14px]">{t(title)}</p>
      {children && <p className="text-fg-muted mt-1 max-w-sm">{children}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

// ── Logo (Original-PNG, transparent) ──────────────────────────────────
// Dark Mode: invert macht die schwarze Wortmarke weiß, hue-rotate(180°) holt das Coral zurück.
export function Logo({ className }: { className?: string }) {
  return (
    <span className={cx("inline-flex items-center", className)}>
      <img src="/logiful-logo.png" alt={t("Logiful")} width={500} height={180} className="h-10 w-auto -my-1.5 -ml-1 select-none dark:invert dark:hue-rotate-180" draggable={false} />
    </span>
  );
}
